import { aplicarIdiomaDoUsuario } from '../../lib/idioma-do-usuario'
import { create } from 'zustand'
import * as Sentry from '@sentry/react'
import { getEngineUrl } from '../../lib/config'
import * as check from './auth-approval-check'

const SESSION_TIMEOUT = 30 * 60 * 1000 // 30 minutes
const STORAGE_KEY_USER = 'axiomgo-userId'

// G2 (falha-fechada): número de tentativas do checkApprovalStatus contra
// /auth/status e o intervalo entre elas antes de assentar um veredito FECHADO
// (nunca 'approved'). 3 tentativas, com 1s e depois 2s de espera entre elas —
// teto de 3s, bem abaixo do limite de ~10s da spec. Isto evita que um soluço
// transitório do servidor (5xx / erro de rede) tranque um usuário legítimo:
// se qualquer tentativa dentro do orçamento responder 200/APPROVED, ele entra.
const APPROVAL_CHECK_MAX_ATTEMPTS = 3
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// #86: single-flight + retry em 2º plano; geração descarta resposta pós-logout.
let approvalCheckInFlight: Promise<void> | null = null
let approvalRetryTimer: ReturnType<typeof setTimeout> | null = null
let approvalFailedCycles = 0
let approvalGeneration = 0

function clearApprovalRetryTimer(): void {
  if (approvalRetryTimer !== null) clearTimeout(approvalRetryTimer)
  approvalRetryTimer = null
}

export type ApprovalStatus = 'unknown' | 'pending' | 'approved' | 'rejected'

/**
 * Normalize the engine's approval status into the frontend ApprovalStatus union.
 * The engine returns the Prisma UserStatus enum UPPERCASE — "APPROVED",
 * "PENDING_APPROVAL", "REJECTED", "SUSPENDED" (handlers.ts: status.toUpperCase()).
 * A raw assignment left approvalStatus='APPROVED' so every `=== 'approved'` gate
 * missed (Defect A2). Mapping rules:
 *  - any "pending*" (engine emits "PENDING_APPROVAL") → 'pending'  (must redirect)
 *  - "suspended" → 'rejected'  (no access; union has no 'suspended')
 *  - "approved" → 'approved'
 *  - unrecognized → 'pending'  (G2, FAIL CLOSED: the engine changed its enum or
 *    sent garbage — that is not a green light. Wait, never enter.)
 */
export function normalizeApprovalStatus(raw: string | null | undefined): ApprovalStatus {
  const s = (raw ?? '').toLowerCase()
  if (s === 'approved') return 'approved'
  if (s.startsWith('pending')) return 'pending'
  if (s === 'rejected' || s === 'suspended') return 'rejected'
  return 'pending'
}

/**
 * Resolve the legacy `VITE_DEFAULT_TENANT` single-tenant fallback.
 *
 * SECURITY (card T2): this fallback is a DEV-ONLY convenience. In production
 * (`import.meta.env.PROD`) it is NEVER honored — a missing Clerk organization
 * must surface as an EMPTY tenantId so the request fails loud (api.ts → 401)
 * and the AuthGuard renders an explicit no-organization error, instead of
 * silently pre-seeding a default tenant and cross-contaminating another
 * tenant's data. Outside PROD it returns the configured fallback (or '').
 */
export function resolveFallbackTenant(): string {
  if (import.meta.env.PROD) return ''
  return import.meta.env.VITE_DEFAULT_TENANT ?? ''
}

/** Function signature matching Clerk's `getToken` */
type GetTokenFn = (options?: { template?: string; skipCache?: boolean }) => Promise<string | null>

interface AuthState {
  userId: string
  tenantId: string
  lastActivity: number
  approvalStatus: ApprovalStatus

  /** #86 — falha de TRANSPORTE em /auth/status; status anterior mantido, retry agendado. */
  approvalCheckDegraded: boolean

  /**
   * UX-only admin flag, derived from the engine `/auth/status` `role` field.
   * Drives the in-app `/admin` route guard and the sidebar gear visibility.
   *
   * SECURITY: this is NOT an authorization gate. The engine `requireAdmin`
   * middleware (401/403) is the real gate on every admin surface (static serve
   * + page REST). Never trust this flag to grant access — it only avoids showing
   * an admin link / route shell to a user the server would reject anyway.
   */
  isAdmin: boolean

  /**
   * Engine base role (`UserRole`: admin | manager | technician | viewer) from
   * `/auth/status`. `null` = not known yet. Single source for UX gates that
   * mirror engine role checks (e.g. schedule write = admin|manager, same as
   * `assertCanWritePlanning`). UX only: the engine 403 is the real gate.
   */
  userRole: string | null

  /** Clerk JWT getter — set by AuthGuard after Clerk loads */
  _getToken: GetTokenFn | null

  /** Sync Clerk user/org state into the store. Called by AuthGuard. */
  syncClerkState: (userId: string, tenantId: string, getToken: GetTokenFn) => void

  /** Get a fresh JWT token for API calls */
  getToken: () => Promise<string | null>

  /** R63 — pede ao Clerk um passe NOVO (ignora o cache). `null` = não deu para renovar. */
  renewToken: () => Promise<string | null>

  /** Check user approval status against the engine backend */
  checkApprovalStatus: () => Promise<void>

  _runApprovalCheck: () => Promise<void>
  refreshActivity: () => void
  logout: () => void
  isSessionExpired: () => boolean
}

/**
 * Centralized authentication store.
 *
 * In Clerk mode, `userId` and `tenantId` come from Clerk's hooks
 * and are synced via `syncClerkState()`. The JWT is obtained on-demand
 * via `getToken()` — no token is stored on the client. The opaque user
 * identifier is persisted in `sessionStorage` (not `localStorage`) so it
 * cannot be exfiltrated by a long-lived XSS payload across browser sessions.
 *
 * The legacy `VITE_DEFAULT_TENANT` env var is used as fallback when
 * no Clerk organization is configured — DEV single-tenant mode ONLY. In
 * production the fallback is dropped (see `resolveFallbackTenant`).
 */
export const useAuthStore = create<AuthState>((set, get) => ({
  userId: '',
  tenantId: resolveFallbackTenant(),
  lastActivity: Date.now(),
  approvalStatus: 'unknown',
  approvalCheckDegraded: false,
  isAdmin: false,
  userRole: null,
  _getToken: null,

  syncClerkState: (userId, tenantId, getToken) => {
    const resolvedTenantId = tenantId || resolveFallbackTenant()
    set({
      userId,
      tenantId: resolvedTenantId,
      _getToken: getToken,
      lastActivity: Date.now(),
    })
    // Persist userId for ConnectionManager bootstrap before Clerk loads.
    // sessionStorage (not localStorage) limits XSS blast radius — the
    // identifier is dropped when the tab/window closes, so a stolen token
    // on a shared shop-floor tablet does not survive across operator shifts.
    sessionStorage.setItem(STORAGE_KEY_USER, userId)
    // Migration: drop any pre-card-148 value still in localStorage so a
    // stale identifier from before this hardening cannot leak through XSS.
    localStorage.removeItem(STORAGE_KEY_USER)
    // Attach opaque IDs to Sentry — no PII (no email, no name)
    Sentry.setUser({ id: userId })
    Sentry.setTag('tenant', resolvedTenantId)
  },

  getToken: async () => {
    const { _getToken } = get()
    if (!_getToken) return null
    try {
      return await _getToken()
    } catch {
      console.warn('[AuthStore] Failed to get Clerk token')
      return null
    }
  },

  renewToken: async () => {
    const { _getToken } = get()
    if (!_getToken) return null
    try {
      return (await _getToken({ skipCache: true })) || null
    } catch {
      console.warn('[AuthStore] Failed to renew Clerk token')
      return null
    }
  },

  checkApprovalStatus: () => {
    if (approvalCheckInFlight) return approvalCheckInFlight
    clearApprovalRetryTimer()
    approvalCheckInFlight = get()
      ._runApprovalCheck()
      .finally(() => {
        approvalCheckInFlight = null
      })
    return approvalCheckInFlight
  },

  _runApprovalCheck: async () => {
    const { tenantId, userId } = get()
    const generation = approvalGeneration

    // SECURITY (card T2): an empty tenant must NEVER produce an 'approved'
    // verdict. This is an AUTHORIZATION check — fail CLOSED. Without an org
    // there is no tenant to authorize against, so reject and send zero network
    // traffic (sending `X-Tenant-Id: ''` to /auth/status is the exact leak T2
    // removes). The AuthGuard already short-circuits this path, but guarding at
    // the store boundary keeps the invariant true regardless of caller.
    if (!tenantId) {
      set({ approvalStatus: 'rejected', isAdmin: false, userRole: null })
      return
    }

    const token = await get().getToken()
    if (!token) {
      // G2, FAIL CLOSED: no token yet is NOT a verdict — never jump to
      // 'approved' to "avoid blocking during dev". Leave approvalStatus
      // untouched (it stays 'unknown', which renders the loading skeleton).
      // AuthGuard re-fires checkApprovalStatus once Clerk delivers a token.
      return
    }

    const baseUrl = getEngineUrl()
    // G2 + #86: 429, 5xx, timeout e erro de rede (TRANSPORTE) são os únicos
    // casos que se re-tentam — um soluço transitório do servidor não pode
    // trancar um usuário legítimo. 401/403/404 e uma resposta 200 nunca
    // re-tentam: são vereditos definitivos do motor (ou a ausência dele).
    let last: check.TransientResult | null = null
    for (let attempt = 1; attempt <= APPROVAL_CHECK_MAX_ATTEMPTS; attempt++) {
      if (last && attempt > 1) {
        const wait = check.inlineWaitMs(attempt, last, Math.random())
        if (wait > check.MAX_INLINE_WAIT_MS) break // espera longa: segundo plano
        await delay(wait)
        if (generation !== approvalGeneration) return
      }
      const result = await check.fetchAuthStatus({ baseUrl, token, tenantId, userId })
      if (generation !== approvalGeneration) return
      if (result.kind !== 'transient') return applyApprovalVerdict(result, tenantId)
      last = result
    }
    onApprovalTransportExhausted(last, tenantId, generation)
  },

  refreshActivity: () => set({ lastActivity: Date.now() }),

  logout: () => {
    approvalGeneration++
    approvalFailedCycles = 0
    approvalCheckInFlight = null
    clearApprovalRetryTimer()
    sessionStorage.removeItem(STORAGE_KEY_USER)
    // Defensive: also clear any pre-card-148 localStorage residue.
    localStorage.removeItem(STORAGE_KEY_USER)
    set({
      userId: '',
      tenantId: resolveFallbackTenant(),
      lastActivity: 0,
      approvalStatus: 'unknown',
      approvalCheckDegraded: false,
      isAdmin: false,
      userRole: null,
      _getToken: null,
    })
    // Clear Sentry identity so subsequent anonymous sessions are not tagged
    Sentry.setUser(null)
  },

  isSessionExpired: () => {
    const { lastActivity } = get()
    return lastActivity > 0 && Date.now() - lastActivity > SESSION_TIMEOUT
  },
}))

/** Veredito do motor (200/401/403/404) — só aqui approvalStatus muda (#86). */
async function applyApprovalVerdict(
  result: Exclude<check.AuthStatusResult, check.TransientResult>,
  tenantId: string,
): Promise<void> {
  const set = useAuthStore.setState
  approvalFailedCycles = 0
  if (result.kind === 'rejected') {
    Sentry.captureException(new Error(`Approval check rejected by engine: HTTP ${result.status}`), { tags: { area: 'auth', tenant: tenantId }, level: 'warning' })
    set({ approvalStatus: 'rejected', approvalCheckDegraded: false, isAdmin: false, userRole: null })
    return
  }
  if (result.kind === 'not_found') {
    // G2, FAIL CLOSED: the endpoint exists — a 404 is an infra anomaly.
    console.warn('[AuthStore] Approval check 404 — endpoint should exist, failing closed')
    Sentry.captureException(new Error('Approval check 404 — endpoint missing, failing closed'), { tags: { area: 'auth', tenant: tenantId }, level: 'error' })
    set({ approvalStatus: 'pending', approvalCheckDegraded: false })
    return
  }
  const data = result.body
  // Engine enum is UPPERCASE — normalize (A2). isAdmin is UX-only.
  const patch: Partial<AuthState> = {
    approvalStatus: normalizeApprovalStatus(data.status),
    approvalCheckDegraded: false,
    isAdmin: data.role === 'admin',
    userRole: data.role ?? null,
  }
  // B2C: sync the real DB tenant UUID (connectionStore.isCrossTenant).
  if (typeof data.tenantId === 'string' && data.tenantId.length > 0) {
    patch.tenantId = data.tenantId
    Sentry.setTag('tenant', data.tenantId)
  }
  set(patch)
  // Fail-soft: idioma nunca derruba o login.
  try {
    await aplicarIdiomaDoUsuario(data.languageEffective, 'selected')
  } catch {
    /* idioma é conforto, não porta: segue */
  }
}

/** Só falha de TRANSPORTE no ciclo: nunca 'approved' (G2) nem 'pending' (#86). */
function onApprovalTransportExhausted(last: check.TransientResult | null, tenantId: string, generation: number): void {
  console.warn('[AuthStore] Approval check transport failure after retries — keeping last status:', last?.status)
  const err = last?.error ?? new Error(`Approval check transient status after retries: HTTP ${last?.status ?? 'n/a'}`)
  Sentry.captureException(err, { tags: { area: 'auth', tenant: tenantId }, level: 'warning' })
  useAuthStore.setState({ approvalCheckDegraded: true })
  approvalFailedCycles++
  clearApprovalRetryTimer()
  approvalRetryTimer = setTimeout(() => {
    approvalRetryTimer = null
    if (generation === approvalGeneration) void useAuthStore.getState().checkApprovalStatus()
  }, check.backgroundWaitMs(approvalFailedCycles, last, Math.random()))
}
